import { inject } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { Observable, map } from 'rxjs';

import { VarAccessService } from '../auth/var-access.service';

// Role/certification route guards. Each composes AFTER authenticatedGuard (which handles
// signed-in / redirect to /login), so they only run for a signed-in principal. The rail
// (VarAccessService.isVar) decides staff vs VAR; for a VAR, the inbox is gated on
// certification. The SERVER independently enforces role + grants on every API, so these
// guards are UX correctness (what a VAR is SHOWN / forced into), not the security boundary.

// /inbox and /tickets/:id — staff always; a VAR only once certified, else forced to the
// certification flow. This is the "no Inbox until certified" rule (@satisfies PR-VARLOGIN-6).
export const inboxAccessGuard = (): boolean | Observable<boolean | UrlTree> => {
  const access = inject(VarAccessService);
  const router = inject(Router);
  if (access.isStaff()) {
    return true;
  }
  return access.ensureCertStatus().pipe(
    map((eligibility) => (eligibility === 'certified' ? true : router.parseUrl('/certification'))),
  );
};

// /var-admin/* — staff only. A VAR is never shown admin surfaces; send them to their own
// home (the inbox if certified, else the certification flow) (@satisfies PR-VARLOGIN-5).
export const staffOnlyGuard = (): boolean | Observable<UrlTree> => {
  const access = inject(VarAccessService);
  const router = inject(Router);
  if (access.isStaff()) {
    return true;
  }
  return access.ensureCertStatus().pipe(
    map((eligibility) => router.parseUrl(eligibility === 'certified' ? '/inbox' : '/certification')),
  );
};

// /certification/* (taker flow) — VARs only. Staff do not take the exam; bounce them to the
// inbox (the admin certification roster is a separate /var-admin route) (@satisfies PR-VARLOGIN-7).
export const certTakerGuard = (): boolean | UrlTree => {
  const access = inject(VarAccessService);
  const router = inject(Router);
  return access.isVar() ? true : router.parseUrl('/inbox');
};
